import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import { useCallback } from 'react';
import * as z from 'zod';

import { TargetBlankMarker } from '#components/target-blank-marker.js';
import { useSchemaLocalStorage } from '#src/utils/use-schema-cookie.js';

import type { OrganizationDocumentStatusForUser } from '@cadstrom/backend/shared';

const DISMISSALS_STORAGE_KEY = 'cadstrom:compliance-banner-dismissals';

/**
 * Dismissal timestamps by `organizationId:versionId`. A dismissal lapses at the
 * end of the local day. The organization is part of the key because the registry
 * publishes one current version to everyone, so keying by version alone would
 * suppress the warning in every other organization too.
 */
const dismissalsSchema = z.record(z.string(), z.string().datetime());
type Dismissals = z.infer<typeof dismissalsSchema>;

export interface ComplianceBannerProps {
	/** Organization the documents belong to; scopes dismissals. */
	organizationId: string;
	/** Display name of the organization the documents belong to. */
	organizationName: string;
	/** Documents awaiting acceptance. Renders nothing when empty. */
	pendingDocuments: OrganizationDocumentStatusForUser['pendingDocuments'];
	/** ISO end of the grace period, or null when none applies. */
	gracePeriodEnd: string | null;
	/** Whether the current user may accept on the organization's behalf. */
	canAccept: boolean;
}

/**
 * Banner warning that an organization has documents to accept before its grace
 * period ends. Dismissals persist per document and lapse the next day.
 *
 * @param props - See {@link ComplianceBannerProps}.
 * @returns The banner overlay, or null when everything is dismissed or absent.
 */
export function ComplianceBanner(props: ComplianceBannerProps) {
	const { organizationId, organizationName, pendingDocuments, gracePeriodEnd, canAccept } = props;
	const [dismissals, setDismissals] = useSchemaLocalStorage<typeof dismissalsSchema>(
		dismissalsSchema,
		DISMISSALS_STORAGE_KEY,
		{},
	);

	const dismiss = useCallback(
		(versionId: string) => {
			setDismissals((current) => ({ ...current, [dismissalKey(organizationId, versionId)]: new Date().toISOString() }));
		},
		[setDismissals, organizationId],
	);

	const now = new Date();
	const visible = pendingDocuments.filter(
		(doc) => !isDismissedToday(dismissals, dismissalKey(organizationId, doc.versionId), now),
	);

	if (visible.length === 0) {
		return null;
	}

	// `pointer-events-none` lets clicks fall through the wrapper's empty margins;
	// each Alert re-enables its own so the close and link still work.
	return (
		<div className='pointer-events-none fixed top-4 right-4 z-1400 flex w-full max-w-sm flex-col gap-2'>
			{visible.map((doc) => (
				<Alert
					key={doc.versionId}
					severity='warning'
					onClose={() => dismiss(doc.versionId)}
					className='shadow-lg pointer-events-auto'
				>
					<AlertTitle>Action required</AlertTitle>
					The {doc.name} has been updated.{' '}
					{canAccept ?
						`You need to accept the new version for ${organizationName}`
					:	`An administrator must accept the new version for ${organizationName}`}
					{gracePeriodEnd === null ? '. ' : ` before ${formatGraceEnd(gracePeriodEnd)}. `}
					<a
						href={doc.url}
						target='_blank'
						rel='noopener noreferrer'
						className='text-blue-11 underline underline-offset-3 hover:text-blue-12'
					>
						Read it
						<TargetBlankMarker />
					</a>
				</Alert>
			))}
		</div>
	);
}

/**
 * @param organizationId - The organization the banner is showing for.
 * @param versionId - The pending document version.
 * @returns The composite dismissal key.
 */
function dismissalKey(organizationId: string, versionId: string): string {
	return `${organizationId}:${versionId}`;
}

/**
 * @param dismissals - Stored dismissal timestamps.
 * @param key - The composite key from {@link dismissalKey}.
 * @param now - The current time.
 * @returns True when a dismissal exists and falls on the same local day.
 */
function isDismissedToday(dismissals: Dismissals, key: string, now: Date): boolean {
	const raw = dismissals[key];
	if (raw === undefined) {
		return false;
	}
	const dismissedAt = new Date(raw);
	if (Number.isNaN(dismissedAt.getTime())) {
		return false;
	}
	return isSameLocalDay(dismissedAt, now);
}

function isSameLocalDay(a: Date, b: Date): boolean {
	return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/**
 * Formats a deadline in UTC — rendering midnight UTC in a western local zone
 * would show the previous day and understate the time remaining.
 *
 * @param iso - ISO 8601 deadline.
 * @returns A human-readable date, or the input unchanged if unparseable.
 */
function formatGraceEnd(iso: string): string {
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) {
		return iso;
	}
	return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
}
